import React, { useEffect, useState, useRef } from 'react'
import { useStatisticsStore } from '@/stores/statistics.store'
import { useKpisStore } from '@/stores/kpis.store'
import { useAuth } from '@/hooks/useAuth'
import { canAccessClients, canAccessKpis, canDownloadApk } from '@/utils/role-access'
import { UsersThree, CheckCircle, XCircle, Clock } from 'phosphor-react'
import { AssignedApkSection } from '@/components/apk/AssignedApkSection'

export const DashboardPage: React.FC = () => {
  const { user } = useAuth()
  const showKpis = canAccessKpis(user)
  const showClientStats = canAccessClients(user)
  const showApkDownload = canDownloadApk(user)

  const globalStatistics = useStatisticsStore(state => state.globalStatistics)
  const isLoading = useStatisticsStore(state => state.isLoading)
  const fetchGlobalStatistics = useStatisticsStore(state => state.fetchGlobalStatistics)
  const kpis = useKpisStore(state => state.kpis)
  const isLoadingKpis = useKpisStore(state => state.isLoading)
  const fetchKpis = useKpisStore(state => state.fetchKpis)
  const evaluateKpi = useKpisStore(state => state.evaluateKpi)

  const [kpiResults, setKpiResults] = useState<Record<string, number>>({})
  const hasFetchedRef = useRef(false)

  useEffect(() => {
    if (hasFetchedRef.current) return
    hasFetchedRef.current = true

    if (showClientStats) {
      fetchGlobalStatistics().catch(error => {
        console.error('Error loading statistics:', error)
      })
    }

    if (showKpis) {
      fetchKpis().catch(error => {
        console.error('Error loading KPIs:', error)
      })
    }
  }, [fetchGlobalStatistics, fetchKpis, showKpis, showClientStats])

  useEffect(() => {
    if (!showKpis) return

    const loadKpiResults = async () => {
      const activeKpis = kpis.filter(kpi => kpi.isActive)
      const results: Record<string, number> = {}

      for (const kpi of activeKpis) {
        try {
          const evaluation = await evaluateKpi(kpi._id)
          if (evaluation && evaluation.length > 0) {
            results[kpi._id] = evaluation.reduce((sum, r) => sum + (r.value || 0), 0)
          }
        } catch (error) {
          console.error(`Error evaluating KPI ${kpi.name}:`, error)
        }
      }

      setKpiResults(results)
    }

    if (kpis.length > 0) {
      loadKpiResults()
    }
  }, [kpis, evaluateKpi, showKpis])

  const statsCards = [
    {
      title: 'Total clients',
      value:
        (globalStatistics?.clients?.ACTIF || 0) +
        (globalStatistics?.clients?.BLOQUÉ || 0) +
        (globalStatistics?.clients?.EXPIRÉ || 0),
      icon: <UsersThree className="w-8 h-8" weight="regular" />,
      color: 'text-[var(--color-info)]',
      bgColor: 'bg-[var(--color-info)]/10',
    },
    {
      title: 'Clients actifs',
      value: globalStatistics?.clients?.ACTIF || 0,
      icon: <CheckCircle className="w-8 h-8" weight="regular" />,
      color: 'text-[var(--color-success)]',
      bgColor: 'bg-[var(--color-success)]/10',
    },
    {
      title: 'Clients bloqués',
      value: globalStatistics?.clients?.BLOQUÉ || 0,
      icon: <XCircle className="w-8 h-8" weight="regular" />,
      color: 'text-[var(--color-error)]',
      bgColor: 'bg-[var(--color-error)]/10',
    },
    {
      title: 'Clients expirés',
      value: globalStatistics?.clients?.EXPIRÉ || 0,
      icon: <Clock className="w-8 h-8" weight="regular" />,
      color: 'text-[var(--color-warning)]',
      bgColor: 'bg-[var(--color-warning)]/10',
    },
  ]

  const activeKpis = kpis.filter(kpi => kpi.isActive)

  if (showClientStats && isLoading && !globalStatistics) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-[var(--color-text-secondary)]">Chargement...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-[var(--color-text-primary)] mb-1">
          Tableau de bord
        </h2>
        <p className="text-sm text-[var(--color-text-secondary)]">
          {showKpis
            ? "Vue d'ensemble des statistiques et indicateurs clés"
            : showClientStats
              ? "Vue d'ensemble de vos statistiques clients"
              : 'Bienvenue sur votre espace de gestion'}
        </p>
      </div>

      {showApkDownload && <AssignedApkSection />}

      {showClientStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsCards.map((card, index) => (
            <div
              key={index}
              className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-text-secondary)] mb-1">{card.title}</p>
                  <p className="text-2xl font-bold text-[var(--color-text-primary)]">
                    {card.value.toLocaleString('fr-FR')}
                  </p>
                </div>
                <div className={`${card.bgColor} ${card.color} p-3 rounded-lg`}>{card.icon}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showKpis && activeKpis.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-[var(--color-text-primary)] mb-4">
            Indicateurs clés de performance
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeKpis.map(kpi => (
              <div
                key={kpi._id}
                className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-[var(--color-text-primary)] mb-1">
                      {kpi.name}
                    </h4>
                    {kpi.description && (
                      <p className="text-sm text-[var(--color-text-secondary)] mb-3">
                        {kpi.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  {kpiResults[kpi._id] !== undefined ? (
                    <p className="text-3xl font-bold text-[var(--color-primary)]">
                      {typeof kpiResults[kpi._id] === 'number'
                        ? kpiResults[kpi._id].toLocaleString('fr-FR')
                        : kpiResults[kpi._id]}
                    </p>
                  ) : (
                    <p className="text-sm text-[var(--color-text-muted)]">Chargement...</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showKpis && activeKpis.length === 0 && !isLoadingKpis && (
        <div className="flex items-center justify-center bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6 h-64">
          <p className="text-sm text-[var(--color-text-secondary)] text-center">
            Aucun indicateur clé de performance actif
          </p>
        </div>
      )}
    </div>
  )
}
